import type { LabResult } from './types'
import { SCENES, type SceneSlug } from './scenes'

export const LAB_PATH = '/lab'
export const PELICAN_PATH = '/lab/pelican-test'
export const RUNNER_PATH = '/lab/endless-runner'

export const scenePath = (scene: SceneSlug = 'pelican-test'): string => SCENES[scene].path

const VENDORS: Record<string, string> = {
  openai: 'OpenAI', anthropic: 'Anthropic', google: 'Google', 'x-ai': 'xAI', deepseek: 'DeepSeek',
  moonshotai: 'Moonshot', 'z-ai': 'Z.ai', minimax: 'MiniMax', xiaomi: 'Xiaomi', qwen: 'Qwen',
  alibaba: 'Alibaba', bytedance: 'ByteDance', 'bytedance-seed': 'ByteDance', tencent: 'Tencent', meta: 'Meta',
  'meta-llama': 'Meta', mistralai: 'Mistral', amazon: 'Amazon', cohere: 'Cohere',
}

const TOKENS: Record<string, string> = {
  gpt: 'GPT', glm: 'GLM', oss: 'OSS', mimo: 'MiMo', kimi: 'Kimi', grok: 'Grok', claude: 'Claude',
  gemini: 'Gemini', deepseek: 'DeepSeek', minimax: 'MiniMax', qwen: 'Qwen', llama: 'Llama', vl: 'VL',
}

/** URL slug for a model id: lowercase, "/" becomes "--". Must stay stable for shared links. */
export function modelSlug(model: string): string {
  return model.toLowerCase().replace(/\//g, '--').replace(/[^a-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '')
}

/** Best-effort inverse of modelSlug for models not present in the summary. */
export function modelFromSlug(slug: string): string {
  return slug.replace('--', '/')
}

export function vendorName(model: string): string {
  const vendor = model.includes('/') ? model.split('/')[0] : ''
  return VENDORS[vendor] ?? (vendor ? vendor.charAt(0).toUpperCase() + vendor.slice(1) : '')
}

/** "openai/gpt-6.1-sol" → "GPT-6.1 Sol". */
export function modelName(model: string): string {
  const name = model.includes('/') ? model.slice(model.indexOf('/') + 1) : model
  const out: string[] = []
  for (const part of name.split('-')) {
    const lower = part.toLowerCase()
    const word = TOKENS[lower] ?? (/^v?\d/.test(lower) ? lower : lower.charAt(0).toUpperCase() + lower.slice(1))
    if (out.length > 0 && /^v?\d/.test(lower) && Object.values(TOKENS).includes(out[out.length - 1])) {
      out[out.length - 1] = `${out[out.length - 1]}-${word}`
    } else {
      out.push(word)
    }
  }
  return out.join(' ')
}

/**
 * "Vendor Model" for display. The vendor is dropped when the model name already
 * starts with it as a whole word: "deepseek/deepseek-v4.1-flash" → "DeepSeek-v4.1 Flash",
 * not "DeepSeek DeepSeek-v4.1 Flash".
 */
export function modelLabel(model: string): string {
  const vendor = vendorName(model)
  const name = modelName(model)
  if (!vendor) return name
  const repeats = name.toLowerCase().startsWith(vendor.toLowerCase()) && !/[a-z]/i.test(name.charAt(vendor.length))
  return repeats ? name : `${vendor} ${name}`
}

export function formatCost(cost: number | null | undefined): string {
  if (cost == null || Number.isNaN(cost)) return '—'
  if (cost === 0) return '$0'
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(3)}`
}

export function formatScore(score: number | null | undefined): string {
  return score == null ? '—' : score.toFixed(1)
}

/** Official production catalog publication timestamp, rendered for compact UI. */
export function formatPublishedAt(value: string | null | undefined): string {
  if (!value) return 'Release date unavailable'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Release date unavailable'
  return `Released ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`
}

export function formatSeconds(sec: number | null | undefined): string {
  if (sec == null) return '—'
  return sec < 60 ? `${sec.toFixed(1)}s` : `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`
}

export type LabBadge = { label: string; tone: 'fame' | 'flop' | 'still' }

/** The sticker on a card: the fun part of every result. */
export function badgeOf(r: Pick<LabResult, 'ok' | 'kind' | 'motion' | 'score'>): LabBadge | null {
  if (!r.ok) return { label: 'Wipeout', tone: 'flop' }
  if (r.kind === 'animated' && r.motion === 0) return { label: 'Frozen', tone: 'still' }
  if (r.score != null && r.score >= 4.5) return { label: 'Masterpiece', tone: 'fame' }
  if (r.score != null && r.score >= 4) return { label: 'Hall of Fame', tone: 'fame' }
  if (r.score != null && r.score <= 2) return { label: 'Abstract art', tone: 'flop' }
  return null
}

export function kindLabel(kind: string): string {
  if (kind === 'animated') return 'Animated'
  if (kind === 'game') return 'Game'
  if (kind === 'toy') return 'Toy'
  return 'Classic'
}

export function resultPath(id: string, scene: SceneSlug = 'pelican-test'): string {
  return `${scenePath(scene)}/r/${encodeURIComponent(id)}`
}

export function runPath(id: string, scene: SceneSlug = 'pelican-test'): string {
  return `${scenePath(scene)}/runs/${encodeURIComponent(id)}`
}

export function testerPath(name: string, scene: SceneSlug = 'pelican-test'): string {
  return `${scenePath(scene)}/u/${encodeURIComponent(name)}`
}

export function modelPath(model: string, scene: SceneSlug = 'pelican-test'): string {
  return `${scenePath(scene)}/m/${modelSlug(model)}`
}

/** Lab's own media (/lab/media/...) and https URLs are rendered; anything else is ignored. */
export function isLabMediaUrl(url: string | undefined, dev = import.meta.env.DEV): url is string {
  if (!url) return false
  if (url.startsWith('/lab/media/')) return true
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:') return true
    return dev && parsed.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(parsed.hostname)
  } catch {
    return false
  }
}

export function shareText(r: Pick<LabResult, 'model' | 'score' | 'ok' | 'classic' | 'title'>, scene: SceneSlug = 'pelican-test'): string {
  const who = modelLabel(r.model)
  if (scene === 'endless-runner') {
    if (!r.ok) return `${who} was asked to build an endless runner game and... wiped out. 🫠`
    return `${who} built an endless runner game${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🎮`
  }
  if (scene === 'watermelon-test') {
    if (!r.ok) return `${who} was asked to build a watermelon rubber-band toy and... it never ran. 🫠`
    return `${who} built a watermelon rubber-band toy${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🍉`
  }
  const what = r.classic ? 'a pelican riding a bicycle' : r.title.toLowerCase()
  if (!r.ok) return `${who} was asked to draw ${what} and... wiped out. 🫠`
  return `${who} drew ${what}${r.score != null ? ` and scored ${r.score.toFixed(1)}/5` : ''}. 🦩🚲`
}
